import { useState, useEffect, useRef, useCallback } from 'react';
import { Input } from './ui/input';
import { Search, X } from 'lucide-react';
import { apiUrl } from '../config/api';

/**
 * SearchBar - just an input. Reports search state to a parent via callbacks.
 * Parent decides how to render results (e.g. replacing the main grid).
 *
 * Searches both local DB and gutendex.com (full Gutenberg catalog) in parallel.
 *
 * Props:
 * - onSearchStateChange({ query, results, isSearching }) called when state changes
 */
export function SearchBar({ onSearchStateChange }) {
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);
  const requestIdRef = useRef(0);

  // Notify parent on every state change
  const reportState = useCallback((nextQuery, nextResults, searching) => {
    if (onSearchStateChange) {
      onSearchStateChange({ query: nextQuery, results: nextResults, isSearching: searching });
    }
  }, [onSearchStateChange]);

  // Debounced search. Local DB (~100ms) returns first and is rendered
  // immediately — the user shouldn't stare at skeleton tiles while Gutendex
  // (~3-7s, sometimes longer) finishes. Gutendex backfills asynchronously
  // and is aborted after GUTENDEX_TIMEOUT_MS so a hung upstream never
  // leaves the UI stuck in its "searching" state.
  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length === 0) {
      reportState('', [], false);
      return;
    }

    const requestId = ++requestIdRef.current;

    const timeoutId = setTimeout(async () => {
      reportState(trimmed, [], true);

      // ─── Local first ───
      let local = [];
      try {
        const res = await fetch(apiUrl(`/api/books/search?q=${encodeURIComponent(trimmed)}`));
        if (res.ok) local = await res.json();
      } catch {
        local = [];
      }
      if (requestId !== requestIdRef.current) return;

      // Render local immediately; isSearching stays true while we still wait
      // on Gutendex. Skeletons hide as soon as results.length > 0, so if
      // local had any hits they're visible right away.
      reportState(trimmed, local, true);

      // ─── Gutendex (with abort) ───
      const GUTENDEX_TIMEOUT_MS = 5000;
      const controller = new AbortController();
      const abortHandle = setTimeout(() => controller.abort(), GUTENDEX_TIMEOUT_MS);

      let gutenberg = [];
      try {
        // Trailing slash is canonical — avoids a 301 redirect (~700ms).
        const res = await fetch(
          `https://gutendex.com/books/?search=${encodeURIComponent(trimmed)}&languages=en`,
          { signal: controller.signal }
        );
        if (res.ok) {
          const data = await res.json();
          gutenberg = data.results || [];
        }
      } catch {
        // Abort or network error — treat as empty; local results are already shown.
        gutenberg = [];
      } finally {
        clearTimeout(abortHandle);
      }
      if (requestId !== requestIdRef.current) return;

      const localGutenbergIds = new Set(local.map(b => b.gutenberg_id));
      const newGutenbergBooks = gutenberg
        .filter(g => !localGutenbergIds.has(g.id))
        .slice(0, 30)
        .map(g => ({
          _isGutenberg: true,
          gutenberg_id: g.id,
          title: g.title,
          author: g.authors?.map(a => a.name).join(', ') || 'Unknown',
          language: 'en',
          status: 'pending',
        }));

      reportState(trimmed, [...local, ...newGutenbergBooks], false);
    }, 350);

    return () => clearTimeout(timeoutId);
  }, [query, reportState]);

  const handleClear = () => {
    setQuery('');
    reportState('', [], false);
    inputRef.current?.focus();
  };

  return (
    <div className="relative w-full max-w-md">
      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
      <Input
        ref={inputRef}
        type="text"
        placeholder="Search 70,000+ books..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        className="pl-10 pr-10"
      />
      {query && (
        <button
          onClick={handleClear}
          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
          aria-label="Clear search"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
